import { useCustomTestWizard } from 'pages/custom_tests/components/custom_test_wizard/context';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { Col, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { FormattedMessage, useIntl } from 'react-intl';

// Categories that carry both “Live in the App” (in-app) and “Browser Based” question
// sets. For these the question bank must be chosen before questions load, because it
// selects which question bank the Questions step pulls (mirrors the legacy behaviour).
const HYBRID_CATEGORY_IDS = [21, 40];

// Question-bank ids for hybrid categories (distinct from the Testing/Training delivery mode).
const BANK_MODE_IN_APP = 1;
const BANK_MODE_BROWSER = 2;

export const DetailsStep = () => {

    const { name, bankModeId, categories, categoryId, handleBankModeChange, handleLanguageChange, handleProductsChange,
        handleToggleCategory, languageId, languages, loadingProducts, nameValidationError, productIds,
        productQuestionCounts, products, programDropdownKey, setProviderState } = useCustomTestWizard();
    const { formatMessage } = useIntl();

    // Hide programs with no eligible questions for the current category/language/bank.
    // Counts are only fetched on the create flows; when absent, every program shows.
    const availablePrograms = productQuestionCounts.length
        ? products.filter(product => (productQuestionCounts.find(count => count.ProductId === product.Id)?.EligibleQuestionCount ?? 0) > 0)
        : products;

    // A category that offers more than one program lets the user combine several programs
    // into one test (multi-select); a single-program category is a single choice. Swap this
    // for a per-category flag (e.g. category.AllowMultiplePrograms) if the rule becomes
    // business-driven rather than availability-driven.
    const allowMultiplePrograms = availablePrograms.length > 1;

    const isHybridCategory = HYBRID_CATEGORY_IDS.includes(categoryId);

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => setProviderState({ name: e.target.value, nameValidationError: '' });

    const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => handleToggleCategory(parseInt(e.target.value, 10));

    const handleLanguageSelect = (e: React.ChangeEvent<HTMLSelectElement>) => handleLanguageChange(parseInt(e.target.value, 10));

    const handleBankModeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => handleBankModeChange(parseInt(e.target.value, 10));

    const handleProgramChange = (value: typeof products[number] | typeof products) => {

        const selected = Array.isArray(value) ? value : (value ? [value] : []);
        handleProductsChange(selected.map(product => product.Id));

    };

    const selectedPrograms = products.filter(product => productIds.includes(product.Id));

    return (
        <div className={'d-flex flex-column gap-4 py-2 mx-auto w-100'} style={{ maxWidth: 720 }}>

            <Row>
                <Col className={'mb-2'} md={6} sm={12}>
                    <GMetrixInput
                        required
                        label={formatMessage({ id: 'test_name.77ea159f', defaultMessage: 'Test Name' })}
                        onChange={handleNameChange}
                        validType={nameValidationError ? 'invalid' : undefined}
                        value={name}
                    />
                    {nameValidationError && (
                        <Form.Text className={'text-danger'}>
                            {nameValidationError}
                        </Form.Text>
                    )}
                </Col>

                <Col className={'mb-2'} md={6} sm={12}>
                    <Form.Label className={'fw-bold d-inline-flex align-items-center gap-1'}>
                        <span>
                            <FormattedMessage defaultMessage="Question Language" id="question_language.dfd461a5" />
                            <span className={'text-danger'}>
                                *
                            </span>
                        </span>
                        <OverlayTrigger
                            overlay={(
                                <Tooltip>
                                    <FormattedMessage defaultMessage="Filters available questions to those supported in the selected language. Students may still choose their display language when taking the test." id="filters_available_questions_to_those_sup.68ba968c" />
                                </Tooltip>
                            )}
                            placement={'top'}
                        >
                            <i className={'fa-light fa-circle-info text-muted'} />
                        </OverlayTrigger>
                    </Form.Label>
                    <Form.Select onChange={handleLanguageSelect} value={languageId}>
                        {/* <option> only renders string children; a React element shows "[object Object]" */}
                        <option value={0}>
                            {formatMessage({ id: 'select.ea1021d6', defaultMessage: 'Select...' })}
                        </option>
                        {languages.map(lang => (
                            <option key={lang.Id} value={lang.Id}>
                                {lang.Name}
                            </option>
                        ))}
                    </Form.Select>
                </Col>
            </Row>

            <section>
                <h6 className={'fw-bold mb-3'}>
                    <FormattedMessage defaultMessage="Select Program" id="select_program.7f418d2a" />
                    <span className={'text-danger'}>
                        *
                    </span>
                </h6>

                <Row>
                    <Col className={'mb-3'} sm={12}>
                        <Form.Label className={'fw-bold'}>
                            <FormattedMessage defaultMessage="Category" id="category.f324da36" />
                        </Form.Label>
                        <Form.Select onChange={handleCategoryChange} value={categoryId}>
                            <option value={0}>
                                {formatMessage({ id: 'select.ea1021d6', defaultMessage: 'Select...' })}
                            </option>
                            {categories.map(category => (
                                <option key={category.Id} value={category.Id}>
                                    {category.Name}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>

                    <Col className={'mb-3'} sm={12}>
                        <Form.Label className={'fw-bold'}>
                            <FormattedMessage defaultMessage="Program" id="program.a0ab1d4c" />
                        </Form.Label>

                        {loadingProducts ?
                            (
                                <div className={'form-control d-flex align-items-center text-muted'}>
                                    <span className={'spinner-border spinner-border-sm me-2'} role={'status'} />
                                    <FormattedMessage defaultMessage="Loading..." id="loading.2350c7f9" />
                                </div>
                            )
                            :
                            (
                                <GMetrixDropdown
                                    filterable
                                    dataItemKey={'Id'}
                                    dataItems={availablePrograms}
                                    disabled={categoryId === 0 || availablePrograms.length === 0}

                                    // Remounts the dropdown when a program change is cancelled in the
                                    // clear-questions dialog, so its internal pending selection
                                    // re-initializes from the unchanged controlled value.
                                    key={programDropdownKey}
                                    multiSelect={allowMultiplePrograms}
                                    onChange={handleProgramChange}
                                    placeholder={formatMessage({ id: 'select_programs.16647040', defaultMessage: 'Select program(s)' })}
                                    textField={'Name'}
                                    value={allowMultiplePrograms ? selectedPrograms : selectedPrograms[0]}
                                    variant={'light'}
                                />
                            )}
                    </Col>
                </Row>

                {isHybridCategory && (
                    <Row>
                        <Col className={'mb-3'} sm={12}>
                            <Form.Label className={'fw-bold d-inline-flex align-items-center gap-1'}>
                                <span>
                                    <FormattedMessage defaultMessage="Test Type" id="test_type.30d1f5ef" />
                                    <span className={'text-danger'}>
                                        *
                                    </span>
                                </span>
                                <OverlayTrigger
                                    overlay={(
                                        <Tooltip>
                                            <FormattedMessage defaultMessage="Determines which question bank is available. “Live in the App” uses in-app questions; “Browser Based” uses browser-delivered questions." id="determines_which_question_bank_is_availa.9ba38db2" />
                                        </Tooltip>
                                    )}
                                    placement={'top'}
                                >
                                    <i className={'fa-light fa-circle-info text-muted'} />
                                </OverlayTrigger>
                            </Form.Label>
                            <Form.Select onChange={handleBankModeSelect} value={bankModeId}>
                                <option value={BANK_MODE_IN_APP}>
                                    {formatMessage({ id: 'live_in_the_app.6c9f2a02', defaultMessage: 'Live in the App' })}
                                </option>
                                <option value={BANK_MODE_BROWSER}>
                                    {formatMessage({ id: 'browser_based.9d2140b1', defaultMessage: 'Browser Based' })}
                                </option>
                            </Form.Select>
                        </Col>
                    </Row>
                )}
            </section>

        </div>
    );

};
